import { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Tab, useRecordContext, useRedirect } from "react-admin";
import { PageHeader } from "@/components/common/PageHeader";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { PageContent } from "@/components/common/PageLayout";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteImpactDialog } from "./components/DeleteImpactDialog";
import { getResourceIcon } from "@/utils/icons";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { PageHeaderActions } from "@/components/common/PageHeaderActions";
import { DocumentDetailsDrawer, DOCUMENT_DETAILS_DRAWER_WIDTH } from "./components/DocumentDetailsDrawer";
import { DocumentProcessingIndicator } from "./components/DocumentProcessingIndicator";
import { DocumentInFlightPanel } from "./components/DocumentInFlightPanel";
import { keepsDetailsDrawer, resolveDocumentShowLayout } from "./utils/documentShowLayout";
import { DocumentReviewTab, SOURCE_PREVIEW_WIDTH } from "./components/document-review/DocumentReviewTab";
import { MultiClientReviewPanel } from "./components/document-review/MultiClientReviewPanel";
import { DocumentFailureBanner } from "./components/DocumentFailureBanner";
import { DocumentRejectedBanner } from "./components/DocumentRejectedBanner";
import { DocumentLoadedBanner } from "./components/DocumentLoadedBanner";
import { DocumentMappingTab } from "./components/mapping-tab/DocumentMappingTab";
import { DocumentResultTab } from "./components/result-tab/DocumentResultTab";
import { AttachmentPreview } from "@/components/common/DocumentsPanel";
import { PreviousLoadsDrawer, PREVIOUS_LOADS_DRAWER_WIDTH } from "./components/result-tab/PreviousLoadsDrawer";
import { useDocumentLoads } from "./components/result-tab/hooks";
import { DocumentRead, DocumentStatus, ResourceName } from "@/types";
import { useDocumentStatusPolling } from "./hooks/useDocumentStatusPolling";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useDownloadDocument } from "@/hooks/useDownloadDocument";
import { triggerBlobDownload } from "@/utils/formatters/fileUtils";
import { composeDocumentLabel, composeDocumentSubtitle } from "@/utils/formatters/documentLabel";

/** Persisted preference key for the document details drawer open/closed
 *  state. Survives navigation so reviewers don't have to re-open it on
 *  every doc. */
const DETAILS_DRAWER_PREF_KEY = "coronis.documentShow.detailsOpen";

/**
 * Read the persisted drawer-open preference. Defaults to `true` so first-time
 * users see the metadata sidebar; subsequent visits respect their last toggle.
 * Falls back to `true` if localStorage is unavailable (private mode, SSR).
 */
const readDetailsDrawerPreference = (): boolean => {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(DETAILS_DRAWER_PREF_KEY);
    if (raw === null) return true;
    return raw === "true";
  } catch {
    return true;
  }
};

const DocumentIcon = getResourceIcon(ResourceName.DOCUMENTS);

// Exported for render-level unit tests of the canDelete entry-point gate
// (PR 23526). Not part of the public component API otherwise.
export const DocumentShowActions = () => {
  const { canDelete } = useResourcePermissions(ResourceName.DOCUMENTS);
  const documentRecord = useRecordContext<DocumentRead>();
  const redirect = useRedirect();
  const { data: downloadBlob, isLoading: isDownloading } = useDownloadDocument(documentRecord?.id || "");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDownload = () => {
    if (!downloadBlob || !documentRecord?.name) return;
    // Compose `${name} — ${sheet_name}` so per-sheet downloads land on disk
    // with distinct filenames. MSW-8 / ADO #4477 AC8.3.
    triggerBlobDownload(downloadBlob, composeDocumentLabel(documentRecord));
  };

  return (
    <>
      {/*
        Documents use a bespoke delete path (#5751): the delete-impact dialog,
        NOT the shared PageHeaderActions `delete`/ConfirmationDialog (which is
        generic across resources and does an immediate REST delete). The generic
        `delete` action is intentionally omitted here.
      */}
      <PageHeaderActions>
        <Tooltip title="Download Original File">
          <span>
            <IconButton
              size="small"
              onClick={handleDownload}
              disabled={isDownloading || !downloadBlob}
              sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {canDelete && (
          <Tooltip title="Delete Document…">
            <span>
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </PageHeaderActions>
      {documentRecord?.id && (
        <DeleteImpactDialog
          open={deleteDialogOpen}
          documentId={String(documentRecord.id)}
          documentName={documentRecord.name}
          onClose={() => setDeleteDialogOpen(false)}
          // Document-only delete: record is gone — leave the (now-404) show page.
          onDeleted={() => redirect("list", ResourceName.DOCUMENTS)}
          // Cascade is fire-and-forget: leave the doomed show page for the list
          // immediately while the delete runs in the background (#6141). The
          // background toast reports progress; the list refreshes on completion.
          onCascadeStarted={() => redirect("list", ResourceName.DOCUMENTS)}
        />
      )}
    </>
  );
};

export const DocumentShow = () => {
  return (
    <StyledShow>
      <DocumentShowContent />
    </StyledShow>
  );
};

const DocumentShowContent = () => {
  const documentRecord = useRecordContext<DocumentRead>();
  const isReviewRequired = documentRecord?.status === DocumentStatus.REVIEW_REQUIRED;
  // Multi-client ("book of business") documents (#5321 / ADR-057) use a dedicated
  // review surface. Branch here — NOT inside DocumentReviewTab — so the
  // single-client orchestrator's per-record hooks never run over a ~50k-row BOB
  // transformation (which freezes the page); the multi-client panel reads the
  // pre-aggregated projection instead.
  const isMultiClient = !!documentRecord?.object_metadata?.is_multi_client;
  // Before review (queued / extracting) the tabs have nothing to show, so the
  // page is one waiting panel under the trail (#7553). After approval the tabs
  // stay: a load is what is coming and Result is where it lands.
  const layout = resolveDocumentShowLayout({
    status: documentRecord?.status,
    isStorageOnly: documentRecord?.object_metadata?.storage_only === true,
    isMultiClient,
  });
  const isWaiting = layout === "waiting";
  // Previous-loads drawer needs the historical loads to render. There are
  // three callers of useDocumentLoads for a given doc — this one (drawer),
  // DocumentResultTab (latest load + previous-loads count), and
  // DocumentMappingTab (latest load → first-record-per-schema for sample
  // values). react-admin's data provider caches by key, so all three share
  // a single underlying fetch.
  const { previousLoads, latestLoad, isLoading: loadsPending } = useDocumentLoads(documentRecord?.id);
  // Claims documents carry their facts in the Result tab's context bar
  // (#7550); the details drawer was looked past, so it is retired for them —
  // but only while that view renders (a load exists). Before the first load,
  // and on failed or rejected documents, the drawer stays (#7553 AC6). The
  // decision waits for the loads query so the drawer does not flash in and out.
  const hasDetailsDrawer = keepsDetailsDrawer(documentRecord, { pending: loadsPending, hasLatestLoad: !!latestLoad });
  const [sourcePreviewOpen, setSourcePreviewOpen] = useState(false);

  // Details drawer state. Lazy-init from localStorage so the user's preference
  // survives navigation. Default open on first visit.
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState<boolean>(() => readDetailsDrawerPreference());
  const [previousLoadsOpen, setPreviousLoadsOpen] = useState(false);

  // Persist on every toggle. Wrapped in try/catch to survive private-mode
  // localStorage rejections without breaking the UI.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(DETAILS_DRAWER_PREF_KEY, String(detailsDrawerOpen));
    } catch {
      // Ignore — toggle still works in-memory for this session.
    }
  }, [detailsDrawerOpen]);

  const toggleDetailsDrawer = useCallback(() => {
    setDetailsDrawerOpen((prev) => !prev);
  }, []);
  const togglePreviousLoads = useCallback(() => {
    setPreviousLoadsOpen((prev) => !prev);
  }, []);

  const { pollIntervalMs, checkNow } = useDocumentStatusPolling(documentRecord?.id, documentRecord?.status);

  // Compute the width reserved on the right by whichever drawer(s) are open.
  // The review state owns its own preview drawer; the standard state owns the
  // details drawer (always-on by default) and the previous-loads drawer
  // (transient). The two standard-state drawers stack side-by-side — previous
  // loads sits to the LEFT of details via `offsetRight`.
  let reservedRight = 0;
  if (isReviewRequired) {
    reservedRight = sourcePreviewOpen ? SOURCE_PREVIEW_WIDTH : 0;
  } else if (isWaiting) {
    reservedRight = 0;
  } else {
    reservedRight =
      (hasDetailsDrawer && detailsDrawerOpen ? DOCUMENT_DETAILS_DRAWER_WIDTH : 0) +
      (previousLoadsOpen ? PREVIOUS_LOADS_DRAWER_WIDTH : 0);
  }

  return (
    <>
      <Box
        sx={{
          width: reservedRight > 0 ? `calc(100% - ${reservedRight}px)` : "100%",
          // minWidth:0 + overflowX:hidden contain any descendant that would
          // otherwise grow past the page width (e.g. a wide review table) and
          // force the scroll to live inside the offending container instead of
          // pushing the whole page sideways.
          minWidth: 0,
          overflowX: "hidden",
          transition: "width 225ms cubic-bezier(0, 0, 0.2, 1)",
        }}
      >
        <PageHeader
          // titleKey preserves PageHeader's `if (!record && titleKey !== "")
          // return null` loading guard so the show page doesn't flash
          // "Unnamed Document" before the record resolves. customTitle is the
          // fallback for genuinely-missing names.
          titleKey="name"
          customTitle="Unnamed Document"
          customSubtitle={composeDocumentSubtitle(documentRecord)}
          statusKey="status"
          rightContent={<DocumentShowActions />}
          hideSectionTitle={true}
          headerIcon={<DocumentIcon sx={{ fontSize: 28, color: "primary.main" }} />}
        />
        {/* In-flight indicator only renders for queued/processing/loading docs.
            For terminal states, the relevant metrics live on the Mapping tab's
            TransformationHeader and the details drawer — no need for a header
            band that duplicates them. Suppressed in review since the review
            surface owns its own progress signals. */}
        {!isReviewRequired && <DocumentProcessingIndicator pollIntervalMs={pollIntervalMs} onCheckNow={checkNow} />}
        <DocumentFailureBanner />
        {/* Why a rejected document loaded nothing (#7441). */}
        <DocumentRejectedBanner />
        {/* What a loaded document loaded and which checks were accepted (#7440 AC7). */}
        <DocumentLoadedBanner />
        <PageContent>
          {isReviewRequired ? (
            isMultiClient ? (
              <MultiClientReviewPanel documentId={documentRecord?.id ? String(documentRecord.id) : undefined} enabled />
            ) : (
              <DocumentReviewTab sourcePreviewOpen={sourcePreviewOpen} onSourcePreviewChange={setSourcePreviewOpen} />
            )
          ) : isWaiting ? (
            <DocumentInFlightPanel />
          ) : (
            <DocumentStandardLayout previousLoadsOpen={previousLoadsOpen} onTogglePreviousLoads={togglePreviousLoads} />
          )}
        </PageContent>
      </Box>
      {/* Drawers render alongside the main content in the standard
          (non-review) state. Previous-loads drawer stacks to the LEFT of
          details via `offsetRight`. */}
      {!isReviewRequired && !isWaiting && (
        <>
          {hasDetailsDrawer && <DocumentDetailsDrawer open={detailsDrawerOpen} onClose={toggleDetailsDrawer} />}
          <PreviousLoadsDrawer
            previousLoads={previousLoads}
            open={previousLoadsOpen}
            onClose={togglePreviousLoads}
            offsetRight={hasDetailsDrawer && detailsDrawerOpen ? DOCUMENT_DETAILS_DRAWER_WIDTH : 0}
          />
        </>
      )}
    </>
  );
};

interface DocumentStandardLayoutProps {
  previousLoadsOpen: boolean;
  onTogglePreviousLoads: () => void;
}

/**
 * Tabbed layout for non-review statuses.
 *
 * Document metadata lives in `DocumentDetailsDrawer`, a right-anchored
 * sidebar rendered alongside this layout by `DocumentShowContent` (replaces
 * the legacy Overview tab). Result and Mapping tabs absorb what the legacy
 * Loaded Records and Applied Mapping tabs surfaced, with substantially
 * richer detail (per-schema cards, list rows, sample column, transformation
 * details dialog, previous-loads dialog).
 *
 * Result and Mapping render for any non-storage-only document; their bodies
 * handle their own empty states (e.g. "no load yet" while UPLOADED) so the
 * tabs are present from the moment the doc lands.
 *
 * A storage-only document (an attachment / reference file, #6505) has no load
 * or mapping, so those tabs are hidden. Rather than leave only "Audit Log" — a
 * blank page for a file whose whole point is being viewed — it gets a default
 * **Document** tab that previews the file (PDF inline, else a download), reusing
 * the same `AttachmentPreview` the policy/claimant panels use.
 */
const DocumentStandardLayout = ({ previousLoadsOpen, onTogglePreviousLoads }: DocumentStandardLayoutProps) => {
  const documentRecord = useRecordContext<DocumentRead>();
  const isStorageOnly = documentRecord?.object_metadata?.storage_only === true;

  const showResultTabs = !isStorageOnly;

  return (
    <StyledTabbedShowLayout>
      {/* Result is the default tab — omit `path` so /documents/{id}/show
          lands here rather than showing a blank pane. Subsequent tabs keep
          their explicit paths so direct URLs work. */}
      {showResultTabs && (
        <Tab label="Result">
          <DocumentResultTab previousLoadsOpen={previousLoadsOpen} onTogglePreviousLoads={onTogglePreviousLoads} />
        </Tab>
      )}
      {showResultTabs && (
        <Tab label="Mapping" path="mapping">
          <DocumentMappingTab />
        </Tab>
      )}
      {/* Storage-only default tab: preview the file itself rather than a load. */}
      {isStorageOnly && documentRecord?.id && (
        <Tab label="Document">
          <Box sx={{ height: "70vh" }}>
            <AttachmentPreview documentId={String(documentRecord.id)} />
          </Box>
        </Tab>
      )}
      <Tab label="Audit Log" path="audit">
        <AuditLogBox tableName={ResourceName.DOCUMENTS} />
      </Tab>
    </StyledTabbedShowLayout>
  );
};
